import _, { set } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import './WorkflowBuilder.scss';
import { actions, days as _days, delay as _delay, outcomes as _outcomes, times } from './const';
import RootContext from '@/RootContext';
import AutoTextarea from '@/components/AutoTextarea';

/**
Here is the reference I'm using for the design.

UI builder

```
[Set Recruiter] ("action": "set_recruiter") To [Chris Faherty] ("recruiter_id": 2)
[Set Study] ("action": "set_study") To [K2 MASH V1] ("study_id": 1)
[Engage Lead] ("action": "engage_lead") Using Agent [k2_mash_v1] ("agent_name": "k2_mash_v1") On [Weekdays] ("days": "MTWRF") Between [9AM] ("start_time": "09:00") And [5PM] ("end_time": "17:00")
[Text Lead] ("action": "text_lead") With Message [] On [Weekdays] ("days": "MTWRF") Between [9AM] ("start_time": "09:00") And [5PM] ("end_time": "17:00")
```

workflows record has regular first-class fields as well as tasks_json to hold a JSON array of tasks.

```
workflows.tasks_json = [
{ "id": 1, "action", "set_recruiter", "recruiter_id": 2 },
{ "id": 2, "action": "set_study", "study_id": 1 },
{ "id": 3, "action": "engage_lead", "agent_name": "k2_mash_v1", "days": "MTWRF", "start_time": "09:00", "end_time": "17:00", "retry_max": 5 },
{ "id": 4, "action": "text_lead", "agent_name": "k2_mash_reminder_v1", "days": "MTWRF", "start_time": "09:00", "end_time": "17:00" }
]
```

subject_workflows record has regular first-class fields, including subject_id and workflow_id, as well as tasks_json to hold a JSON array of tasks state.

```
subject_workflows.tasks_json = [
{ "id": 1, "evidence_start_at": "", "outcome": "Done" },
{ "id": 2, "evidence_start_at": "", "outcome": "Done" },
{ "id": 3, "evidence_start_at": "", "outcome": "Done" },
{ "id": 4, "evidence_start_at": "", "outcome": "Done" }
]
```
*/

export default ({ data, onClose, onSave, supportsTextIntro, textAgentName: _textAgentName, voiceAgentName: _voiceAgentName }) => {
    const rootContext = useContext(RootContext);
    const [purpose, setPurpose] = useState(data?.purpose);
    const [description, setDescription] = useState(data?.description);
    const [mode, setMode] = useState(data?.mode);

    const find_segment_id = id => rootContext.get('segments')?.find(e => e.id === id);

    const actionCount = useRef(0);
    const [action, setAction] = useState(data?.action?.action);
    const [dealId, setDealId] = useState(data?.action?.deal_id);
    const [recruiterId, setRecruiterId] = useState(data?.action?.recruiter_id);
    const [studyId, setStudyId] = useState(data?.action?.study_id);
    const [skipIfAlreadyDone, setSkipIfAlreadyDone] = useState(!!data?.action?.skip_if_already_done);
    const [agentPrecallText, setAgentPrecallText] = useState(data?.action?.agent_precall_text);
    const [textIntro, setTextIntro] = useState(data?.action?.text_intro);
    const [message, setMessage] = useState(data?.action?.message);
    const [days, setDays] = useState(data?.action?.days);
    const [startTime, setStartTime] = useState(data?.action?.start_time);
    const [endTime, setEndTime] = useState(data?.action?.end_time);
    const [outcomes, setOutcomes] = useState(data?.action?.outcomes);
    const [segments, setSegments] = useState(data?.action?.segments && data.action.segments.map(e => find_segment_id(e)));
    const [followups, _setFollowups] = useState(data?.action?.followups && data.action.followups.map(e => {
        const _e = e;
        if (e.segments) _e.segments = e.segments.map(id => typeof id === 'object' ? id : find_segment_id(id));
        return _e;
    }));
    const setFollowups = data => _setFollowups(prevState => {
        let _data = data;
        if (typeof data === 'function') _data = data(prevState);
        _data = Array.isArray(_data) ? _.orderBy(_data, 'delay') : _data;
        return _data;
    });

    // Cascade clear most settings when you change action
    useEffect(() => {
        if (actionCount.current > 0) {
            setDealId();
            setRecruiterId();
            setStudyId();
            setAgentPrecallText();
            setTextIntro();
            setMessage();
            setDays();
            setStartTime();
            setEndTime();
            setFollowups();
        }
    }, [action]);

    const pill = {
        borderRadius: 9999,
        background: '#f4f5f7',
        border: '1px solid #e6e6e6'
    };
    const card = {
        borderRadius: 16
    };

    const handleSave = () => {
        const _action = {
            action,
            ...(dealId && { deal_id: dealId }),
            ...(recruiterId && { recruiter_id: recruiterId }),
            ...(studyId && { study_id: studyId }),
            ...(['engage_lead'].includes(action) && { skip_if_already_done: skipIfAlreadyDone }),
            ...(['engage_lead'].includes(action) && agentPrecallText && { agent_precall_text: agentPrecallText }),
            ...(['engage_lead'].includes(action) && textIntro && { text_intro: textIntro }),
            ...(message && { message }),
            ...(['engage_lead', 'wait'].includes(action) && outcomes && { outcomes }),
            ...(['engage_lead', 'wait'].includes(action) && segments && { segments: segments.map(e => e.id) }),
            ...(['engage_lead'].includes(action) && followups?.length > 0 &&
                {
                    followups: followups.map(e => {
                        return {
                            ...e,
                            ...(e.segments ? { segments: e.segments.map(s => s.id) } : undefined)
                        };
                    })
                }
            ),
            ...(days && { days }),
            ...(startTime && { start_time: startTime }),
            ...(endTime && { end_time: endTime })
        };
        const task = {
            id: data?.id || crypto.randomUUID(),
            purpose: purpose?.trim() || 'Untitled Task',
            ...(description?.trim() ? { description: description?.trim() } : undefined),
            mode,
            action: mode === 'automatic' ? _action : null,
            active: true
        };
        onSave(task);
        onClose();
    };

    const isSaveDisabled = useMemo(() => {
        return !(purpose && purpose.trim().length > 0);
    }, [purpose]);


    const actionDisabled = mode !== 'automatic';

    // Find first non-used delay
    const avail_delay = _delay.find(e => !followups?.find(f => f.delay === e.id))?.id;

    return (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content" style={card}>
                    <div className="modal-body m-4">

                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <h5 className="mb-0" style={{ color: '#2E2868', fontWeight: 'bold' }}>
                                Add Task
                            </h5>
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-muted"
                                onClick={onClose}
                                aria-label="Close"
                                style={{ textDecoration: 'none', fontSize: 40, lineHeight: 1 }}
                            >
                                ×
                            </button>
                        </div>

                        <div className="px-1" style={{ maxHeight: 'calc(100vh - 275px)', overflowY: 'auto' }}>
                            {/* Task Title */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Task Title</label>
                                <input
                                    autoFocus
                                    className="form-control"
                                    defaultValue={purpose}
                                    style={pill}
                                    onChange={e => setPurpose(e.target.value)}
                                />
                            </div>

                            {/* Description */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Description</label>
                                <AutoTextarea
                                    className="form-control"
                                    defaultValue={description}
                                    style={{ ...pill, borderRadius: 12 }}
                                    rows={3}
                                    onChange={e => setDescription(e.target.value)}
                                />
                            </div>

                            {/* Execution Mode */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Execution Mode</label>
                                <select
                                    className="form-select"
                                    defaultValue={mode}
                                    style={pill}
                                    onChange={e => setMode(e.target.value)}
                                >
                                    <option value="manual">Manual</option>
                                    <option value="automatic">Automatic</option>
                                </select>
                            </div>

                            {/* Action builder */}
                            {mode === "automatic" && (
                                <div className="mb-2">
                                    <label className="form-label fw-semibold d-block">Action</label>
                                    <div className="d-flex mx-auto flex-wrap align-items-center gap-2">

                                        {/* Action */}
                                        <div className="row align-items-center">
                                            <div className="col-auto">
                                                <select
                                                    className="form-select"
                                                    defaultValue={action}
                                                    style={{ ...pill, maxWidth: 175 }}
                                                    onChange={e => { actionCount.current++; setAction(e.target.value) }}
                                                >
                                                    <option value="">No Action</option>
                                                    {actions.map((e, i) => (<option key={i} value={e.id}>{e.label}</option>))}
                                                </select>
                                            </div>
                                            {/* [ ] Skip if already done */}
                                            {['engage_lead'].includes(action) && (
                                                <div className="col">
                                                    <label className="form-check-label">
                                                        <input
                                                            className="form-check-input me-2"
                                                            defaultChecked={skipIfAlreadyDone}
                                                            onChange={e => setSkipIfAlreadyDone(e.target.checked)}
                                                            type="checkbox">
                                                        </input>
                                                        Skip if already done
                                                    </label>
                                                    <div className="w-100"></div>
                                                </div>
                                            )}
                                        </div>

                                        {/* To */}
                                        {['set_deal'].includes(action) && (
                                            <>
                                                <span>&nbsp;to&nbsp;</span>
                                                <select
                                                    className="form-select"
                                                    defaultValue={dealId}
                                                    style={{ ...pill, maxWidth: 550 }}
                                                    onChange={e => setDealId(parseInt(e.target.value, 10))}
                                                >
                                                    <option value="">No Deal</option>
                                                    {rootContext.get('deals')?.map((e, i) => (<option key={i} value={e.id}>{e.deal_name}</option>))}
                                                </select>
                                            </>
                                        )}
                                        {['set_recruiter'].includes(action) && (
                                            <>
                                                <span>&nbsp;to&nbsp;</span>
                                                <select
                                                    className="form-select"
                                                    defaultValue={recruiterId}
                                                    style={{ ...pill, maxWidth: 550 }}
                                                    onChange={e => setRecruiterId(parseInt(e.target.value, 10))}
                                                >
                                                    <option value="">No Recruiter</option>
                                                    {rootContext.get('recruiters')?.map((e, i) => (<option key={i} value={e.id}>{e.name}</option>))}
                                                </select>
                                            </>
                                        )}
                                        {['set_study'].includes(action) && (
                                            <>
                                                <span>&nbsp;to&nbsp;</span>
                                                <select
                                                    className="form-select"
                                                    defaultValue={studyId}
                                                    style={{ ...pill, maxWidth: 550 }}
                                                    onChange={e => setStudyId(parseInt(e.target.value, 10))}
                                                >
                                                    <option value="">No Study</option>
                                                    {rootContext.get('studies')?.map((e, i) => (<option key={i} value={e.id}>{e.study_name}</option>))}
                                                </select>
                                            </>
                                        )}

                                        {/* With Precall Text */}
                                        {['engage_lead'].includes(action) && (_voiceAgentName || agentPrecallText) && (
                                            <>
                                                <div className="w-100"></div>
                                                <span>&nbsp;<b>with precall text</b>&nbsp;</span>
                                                <AutoTextarea
                                                    className="form-control"
                                                    defaultValue={agentPrecallText}
                                                    rows="5"
                                                    onChange={e => setAgentPrecallText(e.target.value)}
                                                >
                                                </AutoTextarea>
                                            </>
                                        )}

                                        {/* With Text Intro */}
                                        {['engage_lead'].includes(action) && (supportsTextIntro || textIntro) && (
                                            <>
                                                <div className="w-100"></div>
                                                <span>&nbsp;<b>with text intro</b>&nbsp;</span>
                                                <AutoTextarea
                                                    className="form-control"
                                                    defaultValue={textIntro}
                                                    rows="5"
                                                    onChange={e => setTextIntro(e.target.value)}
                                                >
                                                </AutoTextarea>
                                            </>
                                        )}

                                        {/* With Followup (existing) */}
                                        {['engage_lead'].includes(action) && followups?.length > 0 && (
                                            (followups.map((e, i) => (
                                                <React.Fragment key={e.delay}>
                                                    <div className="w-100"></div>
                                                    <span>&nbsp;<b>on delay</b>&nbsp;</span>
                                                    <select
                                                        className="form-select"
                                                        defaultValue={e.delay}
                                                        onChange={e => {
                                                            setFollowups(prevState => {
                                                                prevState[i].delay = parseInt(e.target.value, 10);
                                                                return [...prevState];
                                                            })
                                                        }}
                                                        style={{ ...pill, maxWidth: 200 }}
                                                    >
                                                        {_delay.map((o, i) => (<option disabled={!!followups.find(f => f.delay === o.id)} key={i} value={o.id}>{o.label}</option>))}
                                                    </select>

                                                    {/* Until outcome/segment is */}
                                                    <>
                                                        <span>&nbsp;<b>and</b>&nbsp;</span>
                                                        <select
                                                            className="form-select"
                                                            defaultValue={(e.outcomes && 'outcome') || (e.segments && 'segment') || ''}
                                                            style={{ ...pill, maxWidth: 210 }}
                                                            onChange={e => {
                                                                setFollowups(prevState => {
                                                                    const p = prevState[i];
                                                                    switch (e.target.value) {
                                                                        case 'outcome':
                                                                            p.outcomes = [];
                                                                            delete p.segments;
                                                                            break;
                                                                        case 'segment':
                                                                            delete p.outcomes;
                                                                            p.segments = [];
                                                                            break;
                                                                        default:
                                                                            delete p.outcomes;
                                                                            delete p.segments;
                                                                    }
                                                                    return [...prevState];
                                                                })
                                                            }}
                                                        >
                                                            <option value="">No other condition</option>
                                                            <option value="outcome">Outcome</option>
                                                            <option value="segment">Segment</option>
                                                        </select>
                                                        {e.outcomes && (
                                                            <>
                                                                <span>&nbsp;<b>is any of</b>&nbsp;</span>
                                                                <Select
                                                                    className="form-control-soft width_300"
                                                                    getOptionLabel={e => e.id}
                                                                    getOptionValue={e => e.id}
                                                                    isMulti
                                                                    menuPlacement="top"
                                                                    name="outcomes"
                                                                    onChange={selected => {
                                                                        setFollowups(prevState => {
                                                                            prevState[i].outcomes = selected.map(e => e.id);
                                                                            return [...prevState];
                                                                        });
                                                                    }}
                                                                    options={_outcomes}
                                                                    value={e.outcomes?.map(e => ({ id: e }))}
                                                                />
                                                            </>
                                                        )}
                                                        {e.segments && (
                                                            <>
                                                                <span>&nbsp;<b>is any of</b>&nbsp;</span>
                                                                <Select
                                                                    className="form-control-soft width_300"
                                                                    getOptionLabel={e => e.name}
                                                                    getOptionValue={e => e.id}
                                                                    isMulti
                                                                    menuPlacement="top"
                                                                    name="segments"
                                                                    onChange={selected => {
                                                                        setFollowups(prevState => {
                                                                            prevState[i].segments = selected;
                                                                            return [...prevState];
                                                                        });
                                                                    }}
                                                                    options={rootContext.get('segments')}
                                                                    value={e.segments}
                                                                />
                                                            </>
                                                        )}
                                                    </>

                                                    <Link
                                                        className="ms-auto"
                                                        onClick={() => setFollowups(prevState => {
                                                            prevState.splice(i, 1);
                                                            return [...prevState];
                                                        })}
                                                    >Remove</Link>
                                                    <div className="w-100"></div>
                                                    <span>&nbsp;<b>with followup text</b>&nbsp;</span>
                                                    <AutoTextarea
                                                        autoFocus
                                                        className="form-control"
                                                        defaultValue={e.text}
                                                        placeholder="Default"
                                                        rows="5"
                                                        onChange={e => setFollowups(prevState => {
                                                            prevState[i].text = e.target.value;
                                                            return [...prevState];
                                                        })}
                                                    >
                                                    </AutoTextarea>
                                                </React.Fragment>
                                            )))
                                        )}

                                        {/* With Followup (new) */}
                                        {['engage_lead'].includes(action) && (
                                            <div className="w-100 mb-3">
                                                {avail_delay &&
                                                    <button
                                                        className="btn btn-secondary mt-3"
                                                        onClick={() => setFollowups(prevState => ([...(prevState ? prevState : []), { delay: avail_delay, text: '' }]))}
                                                    >
                                                        {followups?.length > 0 ? 'Add Another Followup' : 'Add a Followup'}
                                                    </button>
                                                }
                                            </div>
                                        )}

                                        {/* With Message */}
                                        {['text_lead'].includes(action) && (
                                            <>
                                                <div className="w-100"></div>
                                                <span>&nbsp;<b>with message</b>&nbsp;</span>
                                                <AutoTextarea
                                                    className="form-control"
                                                    defaultValue={message}
                                                    rows="5"
                                                    onChange={e => setMessage(e.target.value)}
                                                >
                                                </AutoTextarea>
                                            </>
                                        )}

                                        {/* On */}
                                        {['text_lead', 'engage_lead'].includes(action) && (_voiceAgentName || _textAgentName || message) && (
                                            <>
                                                <span>&nbsp;<b>on</b>&nbsp;</span>
                                                <select
                                                    className="form-select"
                                                    defaultValue={days}
                                                    style={{ ...pill, maxWidth: 200 }}
                                                    onChange={e => setDays(e.target.value)}
                                                >
                                                    {_days.map((e, i) => (<option key={i} value={e.id || ''}>{e.label}</option>))}
                                                </select>
                                            </>
                                        )}

                                        {/* Between */}
                                        {['text_lead', 'engage_lead'].includes(action) && days && (
                                            <>
                                                <span>&nbsp;<b>between</b>&nbsp;</span>
                                                <select
                                                    className="form-select"
                                                    defaultValue={startTime}
                                                    style={{ ...pill, maxWidth: 150 }}
                                                    onChange={e => setStartTime(e.target.value)}
                                                >
                                                    {times.map((e, i) => (<option key={i} value={e.id || ''}>{e.label}</option>))}
                                                </select>
                                                <span>&nbsp;<b>and</b>&nbsp;</span>
                                                <select
                                                    className="form-select"
                                                    defaultValue={endTime}
                                                    style={{ ...pill, maxWidth: 150 }}
                                                    onChange={e => setEndTime(e.target.value)}
                                                >
                                                    {times.map((e, i) => (<option key={i} value={e.id || ''}>{e.label}</option>))}
                                                </select>
                                            </>
                                        )}

                                        {/* Until outcome/segment is */}
                                        {((['engage_lead'].includes(action) && (_voiceAgentName || _textAgentName)) || ['wait'].includes(action)) && (
                                            <>
                                                <div className="w-100"></div>
                                                <select
                                                    className="form-select"
                                                    defaultValue={(outcomes && 'outcome') || (segments && 'segment') || ''}
                                                    style={{ ...pill, maxWidth: 210 }}
                                                    onChange={e => {
                                                        switch (e.target.value) {
                                                            case 'outcome':
                                                                setOutcomes([]);
                                                                setSegments();
                                                                break;
                                                            case 'segment':
                                                                setOutcomes();
                                                                setSegments([]);
                                                                break;
                                                            default:
                                                                setOutcomes();
                                                                setSegments();
                                                        }
                                                    }}
                                                >
                                                    <option value="">Then continue</option>
                                                    <option value="outcome">Wait until outcome</option>
                                                    <option value="segment">Wait until segment</option>
                                                </select>
                                                {outcomes && (
                                                    <>
                                                        <span>&nbsp;<b>is any of</b>&nbsp;</span>
                                                        <Select
                                                            className="form-control-soft width_300"
                                                            getOptionLabel={e => e.id}
                                                            getOptionValue={e => e.id}
                                                            isMulti
                                                            menuPlacement="top"
                                                            name="outcomes"
                                                            onChange={selected => setOutcomes(selected.map(e => e.id))}
                                                            options={_outcomes}
                                                            value={outcomes?.map(e => ({ id: e }))}
                                                        />
                                                    </>
                                                )}
                                                {segments && (
                                                    <>
                                                        <span>&nbsp;<b>is any of</b>&nbsp;</span>
                                                        <Select
                                                            className="form-control-soft width_300"
                                                            getOptionLabel={e => e.name}
                                                            getOptionValue={e => e.id}
                                                            isMulti
                                                            menuPlacement="top"
                                                            name="segments"
                                                            onChange={selected => setSegments(selected)}
                                                            options={rootContext.get('segments')}
                                                            value={segments}
                                                        />
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>

                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="text-center mt-4">
                            <button
                                className="btn"
                                style={{ background: '#2E2868', color: 'white', minWidth: 120 }}
                                disabled={isSaveDisabled}
                                onClick={handleSave}
                            >
                                Save
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};
